import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from "../../environment";
import {map, mergeMap, tap} from "rxjs/operators";
import {
  CalendarEvent,
  CardNegocio,
  CardTips,
  Comentario,
  Comentarios,
  Community,
  Datasheet,
  DatasheetCategory,
  DatasheetTab,
  EstadoCurso,
  FormatoDocumento,
  ItemNegocio,
  itemsArrayServerResponse,
  ItemTip,
  Like,
  Note,
  NoteCategory,
  Preguntas,
  Promotion,
  RegistroPRogresoCurso,
  RegistroVideoCurso,
  Respuestas,
  User,
  UserFavorite,
  UserNote
} from "../../app/interfaces";
import {Observable, Subject} from "rxjs";
import {Storage} from "@ionic/storage";
import {of} from "rxjs/observable/of";

const KEY_NOTAS = 'notas';
const KEY_NOTAS_CATEGORIAS = 'notas_categorias';
const KEY_NOTAS_DE_CATEGORIA = 'notas_de_categoria_';
const KEY_NOTA = 'nota_';

const KEY_FICHAS_CATEGORIAS = 'fichas_categorias';
const KEY_FICHAS_TAB = 'fichas_tab_';
const KEY_TESTIMONIOS_TABS = 'testimonios_tabs';
const KEY_FICHA = 'ficha_';

const KEY_EVENTOS_CALENDARIO = 'eventos_calendario';
const KEY_EVENTO_CALENDARIO = 'eventos_calendario_';

const KEY_PROMOCIONES = 'promociones';
const KEY_PROMOCION = 'promocion_';

const KEY_FORMATOS_Y_DOCUMENTOS = 'formatos_y_documentos';

const KEY_CARDS_NEGOCIOS = 'cards_negocios';
const KEY_CARD_NEGOCIO = 'card_negocio_';
const KEY_ITEM_NEGOCIO = 'item_negocio_';

const KEY_CARDS_TIPS = 'cards_tips';
const KEY_CARD_TIPS = 'card_tips_';
const KEY_ITEM_TIP = 'item_tip_';

const KEY_NOTAS_USUARIO = 'user_notes';

const KEY_TESTIMONIOS = 'testimonios';
const KEY_TESTIMONIO = 'testimonio_';

const KEY_PREGUNTAS = 'preguntas';
const KEY_PREGUNTAS_DE_VIDEO = 'preguntas_video';
const KEY_RESPUESTAS_DE_PREGUNTA = 'respuestas_pregunta';

const KEY_COMENTARIOS_DE_VIDEO = 'comentarios_video';
const KEY_COMENTARIOS_DE_COMENTARIOS = 'comentarios_de_comentarios';
const KEY_LIKES = 'likes';
const KEY_DIPLOMA = 'diploma';
const ESTADO_CURSO = 'estado_del_curso';
const REGISTRO_VIDEO_PROGRESO = 'tiempo_reproducido_del_video';
const REGISTRO_CURSO_PROGRESO = 'cursos_aprovados';

export enum StoragePolicy {
  CacheThenServer,
  ServerOrCache,
  CacheOrServer,
  JustServer,
  JustCache,
}

@Injectable()
export class RestApiProvider {

  eventSubject = new Subject();
  eventObservable = this.eventSubject.asObservable();

  constructor(private httpClient: HttpClient, private storage: Storage) {
  }

  actualizarRegistroProgresoCurso(tip: CardTips, progreso: RegistroPRogresoCurso) 
  {
      return this.httpClient.put(
        environment.serverAddress + 
        `/api/progreso-curso/actualizar-registro/${progreso.id}`,
        {
          tip: tip.id,
        }
      );
  }

  getRegistroProgresoCurso(tip: CardTips,
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
      return this.getSingleFromServer
      <RegistroPRogresoCurso>(`/api/progreso-curso/${tip.id}`, 
      REGISTRO_CURSO_PROGRESO + tip.id.toString(), storagePolicy);
  }

  postPrimerRegistroCursos(tip: CardTips) {
    return this.httpClient.post(
      environment.serverAddress + 
      `/api/progreso-curso/primer-registro/${tip.id}`,
      {
        tip: tip.id,
      }
    );
  }

  postRegistroTiempoDeAvanceVideo(tip: ItemTip,
    registroVideo: RegistroVideoCurso, 
    progreso: number, tiempoActual: number) {
    return this.httpClient.put(
      environment.serverAddress + 
      `/api/progreso-video/registro-avance/${registroVideo.id}`,
      {
        tip: tip.id,
        registro: registroVideo.id,
        progreso: progreso,
        tiempoActual: tiempoActual,
      }
    );
  }

  postPrimerRegistroVideo(tip: ItemTip) {
    return this.httpClient.post(
      environment.serverAddress + 
      `/api/progreso-video/primer-registro/${tip.id}`,
      {
        tip: tip.id,
      }
    );
  }

  getRegistroVideo(tip: ItemTip,
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
      return this.getSingleFromServer
      <RegistroVideoCurso>(`/api/progreso-video/${tip.id}`, 
      REGISTRO_VIDEO_PROGRESO + tip.id.toString(), storagePolicy);
  }

  getEstadoCurso(tip: ItemTip,
  storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer
    <EstadoCurso>(`/api/pdf/estado-curso/${tip.id}`, 
    ESTADO_CURSO + tip.id.toString(), storagePolicy);
  }

  postCursoCompletado(tip: ItemTip) {
    return this.httpClient.post(
      environment.serverAddress + 
      `/api/pdf/curso-completado`,
      {
        tip: tip.id,
      }
    );
  }
  
  getDiploma(user: User, itemTip: ItemTip,
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.httpClient.post(
      environment.serverAddress + 
      `/api/pdf/generar-diploma`,
      {
        usuario: user.id,
        nombre_curso: itemTip.video,
      }
    );
  }

  likeComentarioCDC(comentario: Comentarios, usuario: User, tip: ItemTip) {
    return this.httpClient.post(
      environment.serverAddress +
      `/api/comentarios-de-comentarios-like/like-comentario`,
      {
        id: comentario.id,
        video_id: tip.id,
        usuario: usuario.id
      }
    );
  }

  dislikeComentarioCDC(comentario: Comentarios, like: Like) {
    return this.httpClient.delete(
      environment.serverAddress +
      `/api/comentarios-de-comentarios-like/dislike-comentario/` 
      + like.id.toString() + '/' 
      + comentario.id.toString()
    );
  }
  
  getLikedeUserCDC(tip: ItemTip, user: User, comentario: Comentarios,
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer
    <Like>(`/api/comentarios-de-comentarios-like/get-like-user-coment/${tip.id}
    /${comentario.id}`, 
    KEY_LIKES + tip.id.toString() + comentario.id.toString(), storagePolicy);
  }

  getLikedeUser(tip: ItemTip, user: User, comentario: Comentarios,
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer
    <Like>(`/api/comentarios-like/get-like-user-coment/${tip.id}
    /${comentario.id}`, 
    KEY_LIKES + tip.id.toString() + comentario.id.toString(), storagePolicy);
  }

  getLikesUser(tip: ItemTip, user: User,
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer
    <Like>(`/api/comentarios-like/get-likes-user/${tip.id}`, 
    KEY_LIKES + tip.id.toString(), storagePolicy);
  }

  getLikes(tip: ItemTip, 
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer
    <Like>(`/api/comentarios-like/get-likes/${tip.id}`, 
    KEY_LIKES + tip.id.toString(), storagePolicy);
  }

  dislikeComentario(comentario: Comentarios, like: Like) {
    return this.httpClient.delete(
      environment.serverAddress +
      `/api/comentarios-like/dislike-comentario/` 
      + like.id.toString() + '/' 
      + comentario.id.toString()
    );
  }

  likeComentario(comentario: Comentarios, usuario: User, tip: ItemTip) {
    return this.httpClient.post(
      environment.serverAddress +
      `/api/comentarios-like/like-comentario`,
      {
        id: comentario.id,
        video_id: tip.id,
        usuario: usuario.id
      }
    );
  }

  postComentariodeComentario(data: any, comentario: Comentarios, usuario: User){
    return this.httpClient.post(
      environment.serverAddress + 
      `/api/comentarios-de-comentarios/post-comentario`,
      {
        comentariodecomentario: data,
        comentario: comentario.id,
        usuario: usuario.id,
        nombre: usuario.name,
        avatar: usuario.avatar
      }
    );
  }

  editarComentariodeComentario(data: any, comentarioPrincipal: Comentarios, 
    usuario: User, comentario: Comentarios) {
    return this.httpClient.put(
      environment.serverAddress + 
      `/api/comentarios-de-comentarios/editar-comentario`,
      {
        id: comentario.id,
        comentario: data,
      }
    );
  }

  borrarComentariodeComentario(comentario: Comentarios, 
    comentarioPrincipal: Comentarios) {
    return this.httpClient.delete(
        environment.serverAddress + 
        '/api/comentarios-de-comentarios/borrar/' 
        + comentario.id.toString() + '/' 
        + comentarioPrincipal.id.toString());
  }

  getComentariosdeComentarios(comentario: Comentarios, 
    storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Comentarios>(
      `/api/comentarios-de-comentarios/get-comentarios/${comentario.id}`, 
      KEY_COMENTARIOS_DE_COMENTARIOS + comentario.id.toString(), storagePolicy);
  }
  
  postComentario(data: any, tip: ItemTip, usuario: User) {
    return this.httpClient.post(
      environment.serverAddress + `/api/comentarios/post-comentario`,
      {
        comentario: data,
        tip: tip.id,
        usuario: usuario.id,
        nombre: usuario.name,
        avatar: usuario.avatar
      }
    );
  }

  editarComentario(data: any, tip: ItemTip, usuario: User, 
    comentario: Comentarios) {
    return this.httpClient.put(
      environment.serverAddress + `/api/comentarios/editar-comentario`,
      {
        id: comentario.id,
        comentario: data,
      }
    );
  }

  borrarComentario(comentario: Comentarios) {
    console.log(comentario)
    return this.httpClient.delete(
        environment.serverAddress + 
        '/api/comentarios/borrar/' + comentario.id.toString() + '/' 
        + comentario.video_id.toString());
  }

  getComentarios(tip: ItemTip, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Comentarios>(`/api/comentarios/get-comentarios/${tip.id}`, KEY_COMENTARIOS_DE_VIDEO + tip.id.toString(), storagePolicy);
  }

  getTodasPreguntas(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Preguntas>('/api/preguntas', KEY_PREGUNTAS, storagePolicy);
  }

  getPreguntas(tip: ItemTip, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Preguntas>(`/api/preguntas/${tip.id}`, KEY_PREGUNTAS_DE_VIDEO + tip.id.toString(), storagePolicy);
  }

  getRespuestas(pregunta: Preguntas, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Respuestas>(`/api/preguntas/respuestas/${pregunta.id}`, KEY_PREGUNTAS_DE_VIDEO + pregunta.id.toString(), storagePolicy);
  }

  getListadoNotasAlfabeticamente(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Note>('/api/notes/all', KEY_NOTAS, storagePolicy);
  }

  getListadoCategoriasDeNota(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<NoteCategory>('/api/notes/categories', KEY_NOTAS_CATEGORIAS, storagePolicy);
  }

  getNotasDeCategoriaDeNota(categoria: NoteCategory, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Note>(`/api/notes/categories/${categoria.id}`, KEY_NOTAS_DE_CATEGORIA + categoria.id.toString(), storagePolicy);
  }

  getNota(nota: Note, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer<Note>(`/api/notes/${nota.id}`, KEY_NOTA + nota.id.toString(), storagePolicy);
  }

  guardarNotaEnCache(nota: Note) {
    return this.storage.set(KEY_NOTA + nota.id.toString(), nota);
  }
  guardarFichaEnCache(ficha: Datasheet) {
    return this.storage.set(KEY_FICHA + ficha.id.toString(), ficha);
  }

  getListadoCategoriasDeFicha(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<DatasheetCategory>('/api/datasheets/categories', KEY_FICHAS_CATEGORIAS, storagePolicy);
  }

  getTabDeFichas(tab: DatasheetTab, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer<DatasheetTab>(`/api/datasheets/tabs/${tab.id}`, KEY_FICHAS_TAB + tab.id.toString(), storagePolicy);
  }

  getFicha(ficha: DatasheetTab, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer<Datasheet>(`/api/datasheets/${ficha.id}`, KEY_FICHA + ficha.id.toString(), storagePolicy);
  }

  getListadoEventosDeCalendario(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<CalendarEvent>('/api/events', KEY_EVENTOS_CALENDARIO, storagePolicy);
  }

  getDetallesDeEventoDeCalendario(idEvento: number) {
    return this.getSingleFromServer<CalendarEvent>(`/api/events/${idEvento}`, KEY_EVENTO_CALENDARIO + idEvento.toString(), StoragePolicy.ServerOrCache);
  }

  getTabsDeTestimonios(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<NoteCategory>('/api/community/testimony/tabs', KEY_TESTIMONIOS_TABS, storagePolicy);
  }

  getSearchResults(searchQuery: string) {
    return this.httpClient.get(environment.serverAddress + '/api/search', {
      params: {
        query: searchQuery
      },
      headers: {
        "X-Api-Version": '2',
      },
    })
  }

  getListadoPromociones(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Promotion>('/api/promotions', KEY_PROMOCIONES, storagePolicy);
  }

  getPromocion(promocion: Promotion, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer<Promotion>(`/api/promotions/${promocion.id}`, KEY_PROMOCION + promocion.id.toString(), storagePolicy);
  }

  getListadoFormatosYDocumentos(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<FormatoDocumento>('/api/formats-and-documents', KEY_FORMATOS_Y_DOCUMENTOS, storagePolicy);
  }

  getListadoCardsNegocios(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<CardNegocio>('/api/business/cards', KEY_CARDS_NEGOCIOS, storagePolicy);
  }

  getListadoItemsNegocio(cardNegocio: CardNegocio, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<ItemNegocio>(`/api/business/cards/${cardNegocio.id}`, KEY_CARD_NEGOCIO + cardNegocio.id.toString(), storagePolicy);
  }

  getItemNegocio(itemNegocio: ItemNegocio, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer<ItemNegocio>(`/api/business/${itemNegocio.id}`, KEY_ITEM_NEGOCIO + itemNegocio.id.toString(), storagePolicy);
  }

  getListadoCardsTips(storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<CardTips>('/api/tips/cards', KEY_CARDS_TIPS, storagePolicy);
  }

  getListadoItemTips(cardTips: CardTips, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<ItemTip>(`/api/tips/cards/${cardTips.id}`, KEY_CARD_TIPS + cardTips.id.toString(), storagePolicy);
  }

  getItemTip(itemTip: ItemTip, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer<ItemTip>(`/api/tips/${itemTip.id}`, KEY_ITEM_TIP + itemTip.id.toString, storagePolicy);
  }

  agregarLike(data: { likeOrDislike: number, idTestimonio: number, addOrRemove: boolean }) {
    return this.httpClient.post(environment.serverAddress + `/api/community/add-like`, data);
  }

  agregarTestimonio(data: any) {
    return this.httpClient.post(environment.serverAddress + `/api/community/add-testimony`, data);
  }

  getTestimonios(typeTestimony: string, tabId: number = 0, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getArrayFromServer<Community>(`/api/community/show-testimonies/${typeTestimony}/${tabId}`, KEY_TESTIMONIOS, storagePolicy);
  }

  getTestimonio(idTestimonio: number, storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer) {
    return this.getSingleFromServer<Community>(`/api/community/show-testimony/${idTestimonio}`, KEY_TESTIMONIO + idTestimonio.toString, storagePolicy);
  }

  getListadoFavoritos() {
    return this.httpClient.get<UserFavorite[]>(environment.serverAddress + '/api/user/favorites');
  }

  agregarNotaAFavoritos(nota: Note) {
    const path = '/api/notes/' + nota.id.toString() + '/favorite';
    console.log(environment.serverAddress + path);
    return this.agregarAFavoritos(path);
  }

  eliminarNotaDeFavoritos(nota: Note) {
    const path = '/api/notes/' + nota.id.toString() + '/favorite';
    return this.eliminarDeFavoritos(path);
  }

  agregarFichaAFavoritos(ficha: Datasheet) {
    const path = '/api/datasheets/' + ficha.id.toString() + '/favorite';
    console.log(environment.serverAddress + path);
    return this.agregarAFavoritos(path);
  }

  eliminarFichaDeFavoritos(ficha: Datasheet) {
    const path = '/api/datasheets/' + ficha.id.toString() + '/favorite';
    
    
    return this.eliminarDeFavoritos(path);
  }

  private agregarAFavoritos(ruta: string) {
    return this.httpClient.post(environment.serverAddress + ruta, null);
  }

  private eliminarDeFavoritos(ruta: string) {
    return this.httpClient.delete(environment.serverAddress + ruta);
  }

  syncNotasDelUsuario(notas: UserNote[]) {
    let observable = new Observable((observer) => {
      if (notas == null) {
        this.storage.get(KEY_NOTAS_USUARIO).then((notasFromStorage => {
          notas = notasFromStorage;
          observer.next({
            notas: notasFromStorage,
            fromStorage: true
          });
          observer.complete();
        }));
      } else {
        observer.next({
          notas,
          fromStorage: false
        });
        observer.complete();
      }
    });
    observable = observable.pipe(mergeMap((notas: any) => {
      const body = {
        notes: notas.notas
      };
      if (notas.fromStorage && notas.notas) {
        this.httpClient.post<UserNote[]>(environment.serverAddress + '/api/user/notes', body).subscribe();
        return of(notas.notas);
      } else {
        return this.httpClient.post<UserNote[]>(environment.serverAddress + '/api/user/notes', body)
      }
    }));
    return observable.pipe(tap((notas => {
      this.storage.set(KEY_NOTAS_USUARIO, notas);
    })));
  }

  private getArrayFromServer<T>(relativeUrl, STORAGE_KEY,
                                storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer): Observable<T[]> {
    return new Observable<T[]>((observer) => {
      this.storage.get(STORAGE_KEY).then((tFromStorage: itemsArrayServerResponse<T>) => {

        /** Empezar a procesar lo que está en Storage */
        let hash = '';
        let fromStorageAsT: T[] = [];

        /** Si existe algo en Storage y no sólo estámos pidiendo lo del servidor */
        if (tFromStorage !== null && storagePolicy !== StoragePolicy.JustServer) {
          hash = tFromStorage.hash;
          fromStorageAsT = tFromStorage.models;
          /** Si la condicion no es regresar primero lo del servidor, regresamos lo que tenemos en cache */
          if (storagePolicy !== StoragePolicy.ServerOrCache) {
            observer.next(fromStorageAsT);
            /** Si sólo queremos el cache, o tenemos prioridad sobre lo del servidor, terminamos */
            if (storagePolicy === StoragePolicy.JustCache ||
              storagePolicy === StoragePolicy.CacheOrServer) {
              observer.complete();
              return;
            }
          }
          /** Si no hay cosas en storage y sólo queremos lo que está en cache */
        } else if (storagePolicy === StoragePolicy.JustCache) {
          observer.error();
          observer.complete();
          return;
        }

        /** Hacer request al servidor */
        this.httpClient.get<itemsArrayServerResponse<T>>
        (environment.serverAddress + relativeUrl, {headers: {'X-Hash': hash}}).pipe(map(result => {
          /** Si el resultado del hash fue válido, regresamos lo que teníamos en cache porque es lo
           *  mismo que está en el servidor */
          if (result.valid) {
            return fromStorageAsT;
          }
          /** Guardar los nuevos datos al Storage */
          this.storage.set(STORAGE_KEY, result);

          /** Regresar información del servidor */
          return result.models;
        })).subscribe(result => {
          /** Aquí sólo se debe poder llegar cuando sea una de las siguientes políticas:
           * - CacheThenServer
           * - ServerOrCache (y el servidor respondió)
           * - CacheOrServer (cuando no había nada en cache y el servidor respondió)
           * - JustServer (y el servidor respondió) */
          observer.next(result);
          observer.complete();
        }, (err: HttpErrorResponse) => {
          /** Aquí sólo se llega con las siguientes políticas:
           * - ServerOrCache (y el servidor no respondió
           * - CacheOrServer (si no hay caché y no respondió el servidor)
           * - JustServer (y no respondió el servidor) */

          /** Si la política permite el cache y el servidr no respondió.
           *  Sólo si había algo en Storage */
          if (storagePolicy !== StoragePolicy.JustServer && tFromStorage != null) {
            observer.next(fromStorageAsT);
            observer.complete();
          } else {
            /** Si no había nada en Storage o sólo podíamos checar el servidor */
            observer.error(err);
            observer.complete();
          }
        });
      });
    });
  }

  private getSingleFromServer<T>(relativeUrl, STORAGE_KEY,
                                 storagePolicy: StoragePolicy = StoragePolicy.CacheThenServer): Observable<T> {
    return new Observable<T>((observer) => {
      this.storage.get(STORAGE_KEY).then((tFromStorage: T) => {

        /** Empezar a procesar lo que está en Storage */
        let hash = '';
        let fromStorageAsT: T = null;

        /** Si existe algo en Storage y no sólo estámos pidiendo lo del servidor */
        if (tFromStorage !== null && storagePolicy !== StoragePolicy.JustServer) {
          if (tFromStorage.hasOwnProperty('hash')) {
            // @ts-ignore
            hash = tFromStorage.hash;
          }
          fromStorageAsT = tFromStorage;
          /** Si la condicion no es regresar primero lo del servidor, regresamos lo que tenemos en cache */
          if (storagePolicy !== StoragePolicy.ServerOrCache) {
            observer.next(fromStorageAsT);
            /** Si sólo queremos el cache, o tenemos prioridad sobre lo del servidor, terminamos */
            if (storagePolicy === StoragePolicy.JustCache ||
              storagePolicy === StoragePolicy.CacheOrServer) {
              observer.complete();
              return;
            }
          }
          /** Si no hay cosas en storage y sólo queremos lo que está en cache */
        } else if (storagePolicy === StoragePolicy.JustCache) {
          observer.error();
          observer.complete();
          return;
        }

        /** Hacer request al servidor */
        this.httpClient.get<T>
        (environment.serverAddress + relativeUrl, {headers: {'X-Hash': hash}}).pipe(map((result: T) => {
          /** Si el resultado del hash fue válido, regresamos lo que teníamos en cache porque es lo
           *  mismo que está en el servidor */
          if (result.hasOwnProperty('valid')) {
            return fromStorageAsT;
          }
          /** Guardar los nuevos datos al Storage */
          this.storage.set(STORAGE_KEY, result);

          /** Regresar información del servidor */
          return result;
        })).subscribe(result => {
          /** Aquí sólo se debe poder llegar cuando sea una de las siguientes políticas:
           * - CacheThenServer
           * - ServerOrCache (y el servidor respondió)
           * - CacheOrServer (cuando no había nada en cache y el servidor respondió)
           * - JustServer (y el servidor respondió) */
          observer.next(result);
          observer.complete();
        }, (err: HttpErrorResponse) => {
          /** Aquí sólo se llega con las siguientes políticas:
           * - ServerOrCache (y el servidor no respondió
           * - CacheOrServer (si no hay caché y no respondió el servidor)
           * - JustServer (y no respondió el servidor) */

          /** Si la política permite el cache y el servidr no respondió.
           *  Sólo si había algo en Storage */
          if (storagePolicy !== StoragePolicy.JustServer && tFromStorage != null) {
            observer.next(fromStorageAsT);
            observer.complete();
          } else {
            /** Si no había nada en Storage o sólo podíamos checar el servidor */
            observer.error(err);
            observer.complete();
          }
        });
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/rest-api/rest-api.ts